import * as Yup from 'yup';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { getRequiredText } from '../../../../../global/helpers/transformer';
import { YesNoIndicators } from '../../../../../global/models/api-model';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { PhoneCountryCodeValidationSchema, PhoneValidationSchema } from '../../../../../global/validation-schemas/phone-number-form-validation';
import { BenefitEnrollmentStep } from '../../../../arc/features/new-hire-enrollment/models/pension-enrollment-models';
import { CoveredPerson, Plan, PlanExtended } from '../../../../../Global/models/coverage-model';

export interface MyCpgMidYearState {
    currentStep: BenefitEnrollmentStep;

}

export enum QualifyingEventType {
    marriage = 'marriage',
    divorce = 'divorce',
    legal_separation = 'legal_separation',
    domestic_partnership = 'domestic_partnership',
    domestic_partnership_dissolution = 'domestic_partnership_dissolution',
    legal_child = 'legal_child',
    step_child = 'step_child',
    adopted_child = 'adopted_child',
    judgment_decree_or_order = 'judgment_decree_or_order',
    dependent_gaining_own_benefits = 'dependent_gaining_own_benefits',
    spouse_or_domestic_partner_status_change = 'spouse_or_domestic_partner_status_change',
    loss_of_coverage = 'loss_of_coverage',
    change_in_coverage_cost_or_curtailment = 'change_in_coverage_cost_or_curtailment',
    eligibility_change_in_medicare_medicaid_or_schp = 'eligibility_change_in_medicare_medicaid_or_schp',
    medicare_part_d_enrollment_or_termination = 'medicare_part_d_enrollment_or_termination',
    enrollment_through_health_insurance_exchange = 'enrollment_through_health_insurance_exchange',
}

export enum PointOfContactDepentType {
    Spouse = 'Spouse',
    Child = 'Child',
    DomesticPartner = 'DomesticPartner',
}

export interface MyCpgMidYearCreateEnrollmentPayload {
    enrollment_window: {
        qualifying_event: string,
        qualifying_event_date: string,
    },
}

export enum MyCpgMidYearActions {
    SET_MYCPG_MID_YEAR_CURRENT_STEP = 'SET_MYCPG_MID_YEAR_CURRENT_STEP',
}

export interface MyCPGMidYearConfirmationModel {
    statusCode: string;
    title: string;
    description: string;
    gpsType?: GpsType;
    alertType: string;
    alertDescription: string;
}

export interface PointOfContactPageModel {
    pageTitle: string;
    pageDescription: string;
    isContactPersonLabel: string;
    fullNameContactLabel: string;
    yourContactPhoneLabel: string;
    yourContactEmailLabel: string;
    relationshipToDeceasedLabel: string;
    contactPhoneLabel: string;
    contactEmailLabel: string;
    continueButtonText: string;
    cancelButtonText: string;
    dependentType: PointOfContactDepentType;
}

export interface PointOfContactFormModel {
    is_contact_person: string;
    full_name_contact: string;
    your_contact_phone_formated: string;
    your_contact_phone_country_code: string;
    your_contact_email: string;
    relationship_to_deceased: string;
    contact_phone_number_formated: string;
    contact_phone_country_code: string;
    contact_email: string;
    dependent_type: string;
}

export interface MidYearConfirmationModel {
    hasMedicalAvailable: boolean;
    hasDentalAvailable: boolean;
    hasGroupLife: boolean;
    hasShortTermDisability: boolean;
    hasLongTermDisability: boolean;
    hasUei: boolean;
    hasMedicalEnrolled: boolean;
    hasDentalEnrolled: boolean;
    coveredPeopleMedical: CoveredPerson[];
    coveredPeopleDental: CoveredPerson[];
    selectedMedicalPlan: Plan;
    selectedDentalPlan: Plan;
    selectedGroupLifePlan: PlanExtended;
    selectedShortTermDisability: PlanExtended;
    selectedLongTermDisability: PlanExtended;
    selectedUei: PlanExtended;
    qualifyingEvent: string;
    qualifyingEventDate: string;
    medicalDeclineReason: string;
    medicalDeclineReasonOther: string;
    dentalDeclineReason: string;
    dentalDeclineReasonOther: string;    
}

export interface MyCpgMidYearInitialValues {
    selectedMedicalPlanCode: string | undefined;
    selectedDentalPlanCode: string | undefined;
    selectedGroupPlanCode: string | undefined;
    selectedLtdPlanCode: string | undefined;
    selectedStdPlanCode: string | undefined;
    selectedUeiPlanCode: string | undefined;
    selectedMedicalPlanName: string | undefined;
    selectedDentalPlanName: string | undefined;
    selectedMedicalBillingAccountNumber: string | undefined;
    selectedDentalBillingAccountNumber: string | undefined;
    selectedGroupBillingAccountNumber: string | undefined;
    selectedLtdBillingAccountNumber: string | undefined;
    selectedStdBillingAccountNumber: string | undefined;
    selectedUeiBillingAccountNumber: string | undefined;
    selectedMedicalTier: string | undefined;
    selectedDentalTier: string | undefined;
    medicalDependents: (string | undefined)[]; 
    dentalDependents: (string | undefined)[]; 
    medicalDeclineReason: string | undefined;
    medicalDeclineReasonOther: string; 
    dentalDeclineReason: string | undefined;
    dentalDeclineReasonOther: string;
    specialRequests: string | undefined;
    volume: number | null;
  }

export function GetPointOfContactValidation(labelSetting: LabelSettingProps, pointOfContactPageModel: PointOfContactPageModel) {
    return Yup.lazy(({
        is_contact_person,
        contact_phone_number_formated,
        contact_phone_country_code,
        your_contact_phone_country_code,
        your_contact_phone_formated,
    }) => {

        let schema: Yup.SchemaOf<PointOfContactFormModel> = Yup.object();

        const isContactPerson = is_contact_person === YesNoIndicators.Yes;

        schema = schema.shape({
            is_contact_person: Yup.string().nullable().required(getRequiredText(labelSetting?.requiredFormat, pointOfContactPageModel?.isContactPersonLabel)),
        });

        if (isContactPerson) {
            schema = schema.shape({
                your_contact_email: Yup.string().nullable().required(getRequiredText(labelSetting?.requiredFormat, pointOfContactPageModel?.yourContactEmailLabel)).email(labelSetting.emailFormatError),
                your_contact_phone_formated:
                    PhoneValidationSchema(
                        getRequiredText(labelSetting?.requiredFormat, pointOfContactPageModel?.yourContactPhoneLabel),
                        your_contact_phone_country_code,
                        '',
                        true),
                your_contact_phone_country_code:
                    PhoneCountryCodeValidationSchema(
                        getRequiredText(labelSetting?.requiredFormat, pointOfContactPageModel?.yourContactPhoneLabel),
                        your_contact_phone_formated,
                        true),
            });
        } else {
            schema = schema.shape({
                full_name_contact: Yup.string().nullable().required(getRequiredText(labelSetting?.requiredFormat, pointOfContactPageModel?.fullNameContactLabel)),
                contact_phone_number_formated:
                    PhoneValidationSchema(
                        getRequiredText(labelSetting?.requiredFormat, pointOfContactPageModel?.contactPhoneLabel),
                        contact_phone_country_code,
                        '',
                        true),
                contact_phone_country_code:
                    PhoneCountryCodeValidationSchema(
                        getRequiredText(labelSetting?.requiredFormat, pointOfContactPageModel?.contactPhoneLabel),
                        contact_phone_number_formated,
                        true),
            });
        }

        return schema;
    });
}